/**
 * CRM Leads-by-study view
 *
 * Active leads grouped by the study they are being considered for, one
 * collapsible section per study (from GET /api/crm/studies, count-desc; the
 * two biggest start expanded). Sections lazily fetch their leads on first
 * expand (GET /api/crm/contacts?study=<slug>&sort=activity, paged 25) and by
 * default scope to the "still considering" lifecycles; the Include-closed
 * toggle drops the lifecycle filter and refetches every expanded section.
 * Rows are compact (name + needs-reply dot, lifecycle badge, inline owner,
 * recency) and open the contact detail. Mounted at /crm/by-study.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2, Microscope, Upload, X } from 'lucide-react';
import api from '../../services/api';
import { ContactListItem, ContactRandomizedMembership, StudyLeadStats, StudyOption, contactName, studyInternalLabel } from '../types';
import { CONSIDERING_LIFECYCLES, INCOMING_ELIGIBILITY_OPTIONS, MEMBERSHIP_LIFECYCLES, lifecycleMeta, needsReply, prettySlug, relTime, useIsCRMAdmin, usePersistedState } from '../lib';
import { FilterBar, FilterChip, FilterMultiSelect, FilterSelect, LifecycleBadge, OwnerSelect, PageHeader, RefreshButton } from '../components';
import { eligibilityBadge, RowLink, useRowNav } from '../components/shared';
import ImportLeadsModal from '../components/ImportLeadsModal';
import { Alert } from '../components/ui';

const SECTION_LIMIT = 25;

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

interface Section {
  rows: ContactListItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  /** First page has landed at least once (collapse/expand keeps the rows). */
  loaded: boolean;
  /** Exact per-lifecycle counts (GET /study-lead-stats) — null until loaded. */
  stats: StudyLeadStats | null;
  /**
   * Active per-status list filter (lifecycle_status values), or empty for the
   * default scope (considering, or all when Include-closed is on). Set by
   * toggling breakdown chips; the exact counts in `stats` are never affected.
   */
  statusFilter: string[];
}

/**
 * Multi-study lead lists: a row's status ON THE SECTION'S STUDY, from the
 * memberships the contacts endpoint embeds. Returns null when the section has
 * no crm.studies id (a lead-only slug, which cannot have memberships) or the
 * embed is absent — callers fall back to the contact-level lifecycle_status,
 * which is the pre-feature behavior.
 */
const studyStatusFor = (c: ContactListItem, studyId?: string | null): string | null => {
  if (!studyId || !c.memberships) return null;
  return c.memberships.find((m) => m.study_id === studyId)?.status ?? null;
};

/**
 * Is this lead randomized in some study OTHER than the one whose list we are
 * looking at? Worth flagging on the row: they are enrolled elsewhere, and
 * advancing them here is the thing the randomized guard exists to catch.
 * Returns the offending membership (so the badge can name the study), or null.
 *
 * Reads `randomized_memberships` — the separate batched attachment the contacts
 * endpoint makes under a study scope (plan §4.3) — NOT `memberships`. The old
 * `memberships` read could essentially never fire: that embed is scoped to the
 * SECTION'S study, so its single row always has study_id === studyId. Nothing
 * falls back to it here, because there is nothing it could have told us. A
 * lead-only slug gets neither attachment, so the badge just stays off there.
 */
const randomizedElsewhere = (
  c: ContactListItem,
  studyId?: string | null,
): ContactRandomizedMembership | null =>
  c.randomized_memberships?.find((m) => m.study_id !== studyId) ?? null;

const EMPTY_SECTION: Section = {
  rows: [],
  loading: false,
  loadingMore: false,
  hasMore: false,
  error: null,
  loaded: false,
  stats: null,
  statusFilter: [],
};

/**
 * A "live" study is shown in the working by-study board by default: it is a
 * real CRM study (not a lead-only stub) that is neither archived nor closed.
 * draft/paused stay visible; archived (archived_at set) + status==='closed'
 * hide unless the "Show closed/archived studies" toggle is on. Client-side
 * only — the studies endpoint deliberately returns the full set.
 */
export const isLiveStudy = (s: StudyOption): boolean =>
  s.source === 'crm' && !s.archived_at && s.status !== 'closed';

export default function ByStudy() {
  const navigate = useNavigate();
  const rowNav = useRowNav();
  const isAdmin = useIsCRMAdmin();

  // The study whose import dialog is open (null = closed). Admin-only.
  const [importStudy, setImportStudy] = useState<StudyOption | null>(null);

  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Both toggles persist so the chosen scope survives navigating away and back.
  const [includeClosed, setIncludeClosed] = usePersistedState('bystudy.includeClosed', false);
  // Mirror the (persisted) value so a refetch fired in the same tick as a toggle
  // reads the new scope before the re-render commits it.
  const includeClosedRef = useRef(includeClosed);
  includeClosedRef.current = includeClosed;

  // Study-level visibility toggle (distinct from the lead-lifecycle toggle
  // above). Filter-only: it never refetches, just narrows the in-memory list.
  const [showClosedStudies, setShowClosedStudies] = usePersistedState('bystudy.showClosedStudies', false);

  // Global (view-level) incoming-eligibility filter + lead sort — applied to
  // every expanded section's fetch (the landing-form verdict each lead came in
  // with: eligible / needs review / not eligible / incomplete). Persisted so
  // the chosen scope survives navigating away and back, and mirrored into refs
  // so a refetch fired in the same tick as a change reads the new value before
  // the re-render commits (same pattern as includeClosedRef above).
  const [eligibility, setEligibility] = usePersistedState<string[]>('bystudy.eligibility', []);
  const eligibilityRef = useRef(eligibility);
  eligibilityRef.current = eligibility;
  const [sortMode, setSortMode] = usePersistedState('bystudy.sort', 'activity');
  const sortRef = useRef(sortMode);
  sortRef.current = sortMode;

  // Which study sections are open — persisted per browser so the recruiter's
  // chosen studies stay expanded across navigation (the route unmounts on nav,
  // so plain useState would reset every visit). Stored as a slug array; `null`
  // marks a first-ever visit so loadStudies can apply the two-biggest default —
  // an empty array, by contrast, is a real "collapse everything" choice we keep.
  const [expandedSlugs, setExpandedSlugs] = usePersistedState<string[] | null>('bystudy.expanded', null);
  const expanded = useMemo(() => new Set(expandedSlugs ?? []), [expandedSlugs]);
  // Read the saved value inside loadStudies (a mount-time callback) without
  // making it a dependency — mirrors includeClosedRef above.
  const expandedSlugsRef = useRef(expandedSlugs);
  expandedSlugsRef.current = expandedSlugs;
  // Set-shaped setter over the array store, so the toggle/import call sites below
  // keep working with a Set (has/add/delete) unchanged.
  const setExpanded = useCallback(
    (update: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setExpandedSlugs((prev) => {
        const cur = new Set(prev ?? []);
        const next = typeof update === 'function' ? update(cur) : update;
        return Array.from(next);
      });
    },
    [setExpandedSlugs],
  );
  const [sections, setSections] = useState<Record<string, Section>>({});
  // Latest sections snapshot for imperative reads (the scope-change refetch)
  // without threading them through useCallback dependency lists.
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  // Generation counter: bumping it (Include-closed toggle) invalidates every
  // in-flight section fetch so a stale response can't land in the fresh state.
  const genRef = useRef(0);

  const fetchSection = useCallback(async (slug: string, offset: number, replace: boolean, statusFilter: string[] = []) => {
    const gen = genRef.current;
    setSections((prev) => ({
      ...prev,
      [slug]: {
        ...(prev[slug] || EMPTY_SECTION),
        loading: replace,
        loadingMore: !replace,
        error: null,
        statusFilter,
      },
    }));
    const params: Record<string, string | number> = {
      study: slug,
      // Global sort: 'activity' (default) or 'eligibility' (incoming verdict,
      // actionable-first). Read from the ref so a fetch fired in the same tick
      // as a sort change uses the new order.
      sort: sortRef.current === 'eligibility' ? 'eligibility' : 'activity',
      limit: SECTION_LIMIT,
      offset,
      // By Study is a study ROSTER/reporting view, so archived leads stay in it
      // (archiving only removes a lead from the recruiter's daily follow-up
      // surfaces — inbox/board/worklist). This mirrors study_lead_status_counts,
      // which counts them too; archived rows are tagged in the list below.
      // Snoozed leads stay for the same reason (no show_snoozed=false here): the
      // roster answers "who is on this study", not "who do I work next", and
      // dropping rows the breakdown chips still count would desync the two.
    };
    if (statusFilter.length) {
      // Clicked breakdown chips narrow the list to those statuses (OR'd). They
      // WIN over the include-closed scope, so terminal statuses (Screen Failed,
      // Not eligible, …) can be inspected even with Include-closed off.
      params.lifecycle = statusFilter.join(',');
    } else if (!includeClosedRef.current) {
      params.lifecycle = CONSIDERING_LIFECYCLES.join(',');
    }
    // Global incoming-eligibility filter — independent of the lifecycle scope
    // above (they AND together server-side), so it narrows any section, incl.
    // ones pinned to a lifecycle breakdown chip.
    if (eligibilityRef.current.length) {
      params.eligibility = eligibilityRef.current.join(',');
    }
    try {
      const res = await api.get('/api/crm/contacts', { params });
      if (gen !== genRef.current) return; // stale — the scope changed underneath us
      const batch: ContactListItem[] = res.data?.contacts || [];
      setSections((prev) => {
        const cur = prev[slug] || EMPTY_SECTION;
        // A newer filter change superseded this fetch (a chip click, "Show all",
        // or a switch fired while a Load-more was still in flight) — drop its now
        // wrong-status rows. Runs on BOTH paths: without it the append (Load-more,
        // replace=false) branch would concatenate stale rows onto a list that has
        // since been filtered to a different status.
        if (cur.statusFilter.join(',') !== statusFilter.join(',')) return prev;
        let rows: ContactListItem[];
        if (replace) {
          rows = batch;
        } else {
          // Dedup on append: a lead touched between pages shifts the offset
          // and would repeat the page-boundary rows (duplicate keys).
          const seen = new Set(cur.rows.map((r) => r.id));
          rows = [...cur.rows, ...batch.filter((b) => !seen.has(b.id))];
        }
        return {
          ...prev,
          // Carry stats + filter forward — the success path rebuilds the section
          // object, so an exact-count fetch that landed first must not be wiped.
          [slug]: { rows, loading: false, loadingMore: false, hasMore: batch.length === SECTION_LIMIT, error: null, loaded: true, stats: cur.stats, statusFilter },
        };
      });
    } catch (e: any) {
      if (gen !== genRef.current) return;
      setSections((prev) => ({
        ...prev,
        [slug]: { ...(prev[slug] || EMPTY_SECTION), loading: false, loadingMore: false, error: errMsg(e) },
      }));
    }
  }, []);

  /**
   * Exact per-lifecycle lead counts for one study (replaces the paginated
   * "25+ considering" guess). Non-fatal: a failure just leaves the list without
   * the count chips. Guarded by genRef like fetchSection.
   */
  const fetchStats = useCallback(async (slug: string) => {
    const gen = genRef.current;
    try {
      const res = await api.get('/api/crm/study-lead-stats', { params: { study: slug } });
      if (gen !== genRef.current) return;
      const stats: StudyLeadStats = res.data;
      setSections((prev) => ({
        ...prev,
        [slug]: { ...(prev[slug] || EMPTY_SECTION), stats },
      }));
    } catch {
      /* non-fatal — the section still renders without exact counts */
    }
  }, []);

  const loadStudies = useCallback(async (preserveExpanded = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/studies');
      const list: StudyOption[] = ((res.data?.studies || []) as StudyOption[])
        .slice()
        .sort((a, b) => (b.count || 0) - (a.count || 0));
      setStudies(list);
      if (!preserveExpanded) {
        const saved = expandedSlugsRef.current;
        if (saved != null) {
          // Returning visit: restore the recruiter's saved open sections. Drop
          // slugs whose study no longer exists, then lazily fetch what's open.
          const known = new Set(list.map((s) => s.slug));
          const valid = saved.filter((slug) => known.has(slug));
          if (valid.length !== saved.length) setExpanded(new Set(valid));
          valid.forEach((slug) => { fetchSection(slug, 0, true); fetchStats(slug); });
        } else {
          // First-ever visit: the two biggest LIVE studies start expanded; the
          // rest collapsed. Pick from the live-filtered list so an archived/closed
          // study (hidden by default) is never auto-expanded. When there are none
          // to open, leave the store as-is so a later visit still gets this default.
          const first = list.filter(isLiveStudy).slice(0, 2).map((s) => s.slug);
          if (first.length > 0) {
            setExpanded(new Set(first));
            first.forEach((slug) => { fetchSection(slug, 0, true); fetchStats(slug); });
          }
        }
      }
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [fetchSection, fetchStats, setExpanded]);

  useEffect(() => { loadStudies(); }, [loadStudies]);

  /** Header Refresh: re-run the studies fetch and refetch every expanded section. */
  const refresh = () => {
    genRef.current++;
    setSections({});
    if (expanded.size > 0) {
      loadStudies(true);
      expanded.forEach((slug) => { fetchSection(slug, 0, true); fetchStats(slug); });
    } else {
      loadStudies();
    }
  };

  const toggleSection = (slug: string) => {
    const isOpen = expanded.has(slug);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (isOpen) next.delete(slug); else next.add(slug);
      return next;
    });
    if (!isOpen) {
      const sec = sections[slug];
      if (!sec || (!sec.loaded && !sec.loading)) fetchSection(slug, 0, true);
      if (!sec || !sec.stats) fetchStats(slug);
    }
  };

  /**
   * Click a per-status breakdown chip: filter the section's list to just that
   * lifecycle status, or clear the filter when the already-active chip is
   * clicked again (back to the default considering / all scope).
   */
  const toggleStatusFilter = (slug: string, status: string) => {
    const cur = sections[slug]?.statusFilter ?? [];
    const next = cur.includes(status) ? cur.filter((s) => s !== status) : [...cur, status];
    fetchSection(slug, 0, true, next);
  };

  /** Flip the lifecycle scope: drop everything loaded, refetch open sections. */
  const toggleIncludeClosed = () => {
    const next = !includeClosed;
    setIncludeClosed(next);
    includeClosedRef.current = next;
    genRef.current++;
    setSections({});
    expanded.forEach((slug) => { fetchSection(slug, 0, true); fetchStats(slug); });
  };

  /**
   * Re-run every expanded section after a global scope change (the eligibility
   * filter or the sort). Unlike the include-closed toggle, this preserves each
   * section's active lifecycle breakdown chip and its exact counts — only the
   * row list is refetched. Cached rows for collapsed sections are dropped (stats
   * kept) so they refetch under the new scope on the next expand.
   */
  const refetchForScope = () => {
    genRef.current++;
    const cur = sectionsRef.current;
    setSections((prev) => {
      const next: Record<string, Section> = {};
      for (const [slug, sec] of Object.entries(prev)) {
        next[slug] = { ...EMPTY_SECTION, stats: sec.stats, statusFilter: sec.statusFilter };
      }
      return next;
    });
    expanded.forEach((slug) => fetchSection(slug, 0, true, cur[slug]?.statusFilter ?? []));
  };

  const applyEligibility = (next: string[]) => {
    setEligibility(next);
    eligibilityRef.current = next;
    refetchForScope();
  };

  const applySort = (next: string) => {
    setSortMode(next);
    sortRef.current = next;
    refetchForScope();
  };

  /** After a list import: refresh totals + the section's rows and exact counts. */
  const onImported = (slug: string) => {
    loadStudies(true);
    fetchSection(slug, 0, true);
    fetchStats(slug);
    setExpanded((prev) => (prev.has(slug) ? prev : new Set(prev).add(slug)));
  };

  const onOwnerChanged = (slug: string, id: string, owner: string | null) =>
    setSections((prev) => {
      const cur = prev[slug];
      if (!cur) return prev;
      return {
        ...prev,
        [slug]: { ...cur, rows: cur.rows.map((r) => (r.id === id ? { ...r, owner_user_id: owner } : r)) },
      };
    });

  // Study-level visibility filter, derived at render so toggling needs no
  // refetch (the full count-desc list is already in memory).
  const visibleStudies = showClosedStudies ? studies : studies.filter(isLiveStudy);

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Microscope}
        title="Leads by study"
        subtitle="Active leads grouped by the study they are being considered for."
        actions={<RefreshButton onClick={refresh} loading={loading} label="Refresh studies" />}
      />

      {/* Toolbar: incoming-eligibility filter + sort, then lifecycle/study
          visibility toggles. The filter + sort apply to every expanded study. */}
      <FilterBar>
        <FilterMultiSelect
          auto
          label="Incoming eligibility"
          placeholder="Any eligibility"
          title="Filter every study by the eligibility each lead came in with"
          values={eligibility}
          onChange={applyEligibility}
          options={[{ value: '', label: 'Any eligibility' }, ...INCOMING_ELIGIBILITY_OPTIONS]}
        />
        <FilterSelect
          auto
          label="Sort leads"
          title="Sort leads within each study"
          value={sortMode}
          onChange={applySort}
          options={[
            { value: 'activity', label: 'Recent activity' },
            { value: 'eligibility', label: 'By eligibility' },
          ]}
        />
        <FilterChip active={includeClosed} onClick={toggleIncludeClosed}>
          Include closed leads
        </FilterChip>
        <FilterChip active={showClosedStudies} onClick={() => setShowClosedStudies((v) => !v)}>
          Show closed/archived studies
        </FilterChip>
      </FilterBar>

      {error && (
        <Alert
          tone="error"
          icon={false}
          actions={
            <button
              onClick={refresh}
              className="inline-flex min-h-[44px] items-center rounded-lg border border-red-300 px-3 py-1 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 sm:min-h-0"
            >
              Retry
            </button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>
      ) : visibleStudies.length === 0 ? (
        !error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Microscope className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
            <h3 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">
              {studies.length === 0 ? 'No studies yet' : 'No active studies'}
            </h3>
            <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
              {studies.length === 0
                ? 'No studies yet — leads will group here by study.'
                : "No active studies — turn on “Show closed/archived studies” to see archived/closed ones."}
            </p>
          </div>
        )
      ) : (
        <div className="space-y-3">
          {visibleStudies.map((s) => {
            const isOpen = expanded.has(s.slug);
            const sec = sections[s.slug] || EMPTY_SECTION;
            return (
              <div key={s.slug} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                {/* Section header (toggle + optional admin import action) */}
                <div className="flex items-stretch">
                  <button
                    onClick={() => toggleSection(s.slug)}
                    aria-expanded={isOpen}
                    className="flex min-h-[44px] flex-1 items-center gap-2 px-3 py-2.5 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20 sm:min-h-0 sm:px-4"
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                    )}
                    <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {studyInternalLabel(s) || prettySlug(s.slug)}
                    </span>
                    <span className="hidden truncate text-xs text-gray-400 dark:text-gray-500 sm:inline">{s.slug}</span>
                    <span className="ml-auto flex flex-shrink-0 items-center gap-1.5">
                      {/* Exact (not paginated) count of leads still being
                          considered — see the per-status breakdown when open. */}
                      {sec.stats && (
                        <span
                          title="Leads still being considered for this study"
                          className="rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                        >
                          {sec.stats.considering.toLocaleString()} considering
                        </span>
                      )}
                      {/* All-time. Under the membership read path stats.total
                          is "everyone ever considered for this study" —
                          including leads who since pivoted away, who used to
                          vanish from this number entirely. s.count (the lead-row
                          tally) is the fallback until stats load. */}
                      <span
                        title={sec.stats
                          ? 'Everyone ever considered for this study'
                          : 'All leads ever for this study'}
                        className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      >
                        {sec.stats
                          ? `${sec.stats.total.toLocaleString()} all-time`
                          : `${s.count.toLocaleString()} total`}
                      </span>
                    </span>
                  </button>
                  {isAdmin && s.source === 'crm' && (
                    <button
                      type="button"
                      onClick={() => setImportStudy(s)}
                      title="Import leads"
                      aria-label={`Import leads into ${studyInternalLabel(s) || prettySlug(s.slug)}`}
                      className="flex min-h-[44px] w-11 flex-shrink-0 items-center justify-center border-l border-gray-100 text-gray-400 transition-colors duration-150 hover:bg-gray-50 hover:text-primary-600 focus:outline-none focus-visible:bg-primary-50 dark:border-gray-700 dark:hover:bg-gray-700/50 dark:hover:text-primary-400 sm:min-h-0 sm:w-10"
                    >
                      <Upload className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Section body (lazy) */}
                {isOpen && (
                  <div className="border-t border-gray-100 dark:border-gray-700">
                    {/* Exact per-status breakdown (every status with ≥1 lead).
                        Each chip is a filter: click to scope the list below to
                        just that status, click again (or "Show all") to clear.
                        The counts themselves are unaffected by the filter.

                        MEMBERSHIP_LIFECYCLES, not LIFECYCLES: by_status counts
                        study-list MEMBERSHIPS, which can sit at the two terminals
                        (completed / discontinued) a contact never reaches. On the
                        contact-only list those leads were counted in `total` but
                        had no chip — invisible, and unfilterable. */}
                    {sec.stats && sec.stats.total > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-100 px-3 py-2 dark:border-gray-700 sm:px-4">
                        {MEMBERSHIP_LIFECYCLES.filter((l) => (sec.stats!.by_status[l.value] || 0) > 0).map((l) => {
                          const active = sec.statusFilter.includes(l.value);
                          const dimmed = sec.statusFilter.length > 0 && !active;
                          return (
                            <button
                              key={l.value}
                              type="button"
                              onClick={() => toggleStatusFilter(s.slug, l.value)}
                              aria-pressed={active}
                              title={active ? `Showing only ${l.label} — click to show all` : `Show only ${l.label} leads`}
                              className={`inline-flex min-h-[44px] items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:min-h-0 ${l.badge} ${active ? 'ring-2 ring-primary-500 ring-offset-1 dark:ring-offset-gray-800' : ''} ${dimmed ? 'opacity-50 hover:opacity-100' : ''}`}
                            >
                              {l.label}
                              <span className="font-semibold tabular-nums">{(sec.stats!.by_status[l.value] || 0).toLocaleString()}</span>
                            </button>
                          );
                        })}
                        {sec.statusFilter.length > 0 && (
                          <button
                            type="button"
                            onClick={() => fetchSection(s.slug, 0, true, [])}
                            title="Clear the status filter"
                            className="inline-flex min-h-[44px] items-center gap-1 rounded-full border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                          >
                            <X className="h-3 w-3" />
                            Show all
                          </button>
                        )}
                      </div>
                    )}
                    {sec.loading ? (
                      <div className="flex items-center justify-center py-8"><Loader2 className="h-4 w-4 animate-spin text-primary-600" /></div>
                    ) : sec.error ? (
                      <div className="flex flex-wrap items-center gap-2 px-3 py-3 text-sm text-red-700 dark:text-red-400 sm:px-4">
                        <span className="min-w-0">{sec.error}</span>
                        <button
                          onClick={() => fetchSection(s.slug, 0, true, sec.statusFilter)}
                          className="inline-flex min-h-[44px] items-center rounded-lg border border-red-300 px-3 py-1 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 sm:min-h-0"
                        >
                          Retry
                        </button>
                      </div>
                    ) : sec.rows.length === 0 ? (
                      <p className="px-3 py-3 text-sm text-gray-400 dark:text-gray-500 sm:px-4">
                        {sec.statusFilter.length === 1
                          ? `No ${lifecycleMeta(sec.statusFilter[0]).label} leads for this study.`
                          : sec.statusFilter.length > 1
                            ? 'No matching leads for this study.'
                            : includeClosed ? 'No leads for this study.' : 'No leads currently being considered.'}
                      </p>
                    ) : (
                      <>
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                          {sec.rows.map((c) => {
                            const reply = needsReply(c.last_comm);
                            // Landing-form verdict the lead came into the CRM with.
                            const elig = eligibilityBadge(c.incoming_eligibility ?? null);
                            // Enrolled in ANOTHER study — the attachment carries
                            // the study, so the warning can name it instead of
                            // making the recruiter open the lead to find out.
                            const randomizedIn = randomizedElsewhere(c, s.id);
                            const randomizedInLabel = randomizedIn?.study.title || null;
                            const randomizedInText = randomizedInLabel
                              ? `Randomized in ${randomizedInLabel}`
                              : 'Randomized in another study';
                            return (
                              <div
                                key={c.id}
                                role="button"
                                tabIndex={0}
                                {...rowNav(`/crm/contacts/${c.id}`)}
                                onKeyDown={(e) => {
                                  // Only when the row itself is focused — Enter/Space
                                  // on the embedded select must not navigate.
                                  if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                                    e.preventDefault();
                                    navigate(`/crm/contacts/${c.id}`);
                                  }
                                }}
                                className="flex min-h-[44px] cursor-pointer flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20 sm:min-h-0 sm:px-4"
                              >
                                <span className="flex min-w-0 items-center gap-2">
                                  <RowLink
                                    to={`/crm/contacts/${c.id}`}
                                    title={contactName(c)}
                                    className={`truncate text-sm text-gray-900 dark:text-gray-100 ${reply ? 'font-semibold' : 'font-medium'}`}
                                  >
                                    {contactName(c)}
                                  </RowLink>
                                  {reply && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" title="Needs your reply" />}
                                </span>
                                {/* Multi-study lead lists: on study X's list, show
                                    their status FOR X — not the contact-level
                                    pipeline stage, which describes whichever
                                    study they are currently being worked for.
                                    Lead-only slugs have no crm.studies row and
                                    so no memberships; they fall back. */}
                                <LifecycleBadge
                                  status={studyStatusFor(c, s.id) ?? c.lifecycle_status}
                                  className="flex-shrink-0"
                                />
                                {randomizedIn && (
                                  <span
                                    title={randomizedInText}
                                    aria-label={randomizedInText}
                                    className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                                  >
                                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                    Randomized elsewhere
                                  </span>
                                )}
                                {elig && (
                                  <span
                                    title="Eligibility this lead came into the CRM with"
                                    className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${elig.cls}`}
                                  >
                                    {elig.label}
                                  </span>
                                )}
                                {c.archived_at && (
                                  <span
                                    title="Archived — removed from daily follow-up lists, but still part of this study's roster and counts"
                                    className="inline-flex flex-shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                                  >
                                    Archived
                                  </span>
                                )}
                                <span className="ml-auto flex flex-shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                  <OwnerSelect
                                    contactId={c.id}
                                    value={c.owner_user_id}
                                    compact
                                    onChanged={(o) => onOwnerChanged(s.slug, c.id, o)}
                                  />
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    {relTime(c.last_activity_at || c.created_at)}
                                  </span>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {sec.hasMore && (
                          <div className="flex justify-center border-t border-gray-100 px-3 py-2 dark:border-gray-700">
                            <button
                              onClick={() => fetchSection(s.slug, sec.rows.length, false, sec.statusFilter)}
                              disabled={sec.loadingMore}
                              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                            >
                              {sec.loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                              {sec.loadingMore ? 'Loading…' : 'Load more'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {importStudy && (
        <ImportLeadsModal
          study={{ slug: importStudy.slug, title: studyInternalLabel(importStudy), id: importStudy.id }}
          open={!!importStudy}
          onClose={() => setImportStudy(null)}
          onImported={() => onImported(importStudy.slug)}
        />
      )}
    </div>
  );
}
